/**
 * The web view's physics: a small force layout, kept free of Vue and the DOM so
 * it can be stepped in a test.
 *
 * Every node pushes every other away (inverse square, cut off past `CUTOFF`),
 * links pull their ends toward a rest length, a weak spring draws everything to
 * the origin so loose trees do not drift off, and overlapping circles are
 * pushed apart. All of it is scaled by `alpha`, which cools toward zero, so the
 * layout settles and the animation loop can stop; anything that changes the
 * graph reheats it.
 *
 * Positions are never saved: the layout is meant to rearrange itself.
 */

export interface PNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** Held where it is (a node being dragged): forces neither move it nor stop at it. */
  fixed: boolean;
}

export interface PLink {
  a: string;
  b: string;
  /** Rest length, between the circles' edges. */
  len: number;
  /** Stiffness, 0..1. */
  k: number;
}

/** What a node should be. A new one is seated beside `near`, if that node exists. */
export interface NodeSpec {
  id: string;
  r: number;
  near?: string | null;
}

export interface Sim {
  nodes: Map<string, PNode>;
  links: PLink[];
  alpha: number;
}

const REPULSION = 2400;
const CUTOFF = 600;
const GRAVITY = 0.012;
const DAMPING = 0.82;
const MAX_SPEED = 40;
const COOLING = 0.985;
/** Below this the layout is settled. */
export const ALPHA_MIN = 0.004;

export function createSim(): Sim {
  return { nodes: new Map(), links: [], alpha: 1 };
}

/** Wake the layout up again, to at least `to`. */
export function reheat(sim: Sim, to = 0.6): void {
  sim.alpha = Math.max(sim.alpha, to);
}

/**
 * Make the sim hold exactly these nodes and links. A node still there keeps its
 * place and speed; one that has gone is dropped; a new one is seated beside
 * `near` with a little jitter (so two siblings do not land on the same spot and
 * never separate), or on a ring around the origin when it has nobody to sit by.
 * Returns whether the set of nodes or links changed; if so the sim is reheated.
 */
export function reconcile(sim: Sim, specs: NodeSpec[], links: PLink[], rand: () => number = Math.random): boolean {
  let changed = false;
  const wanted = new Set(specs.map((s) => s.id));
  for (const id of sim.nodes.keys()) {
    if (!wanted.has(id)) {
      sim.nodes.delete(id);
      changed = true;
    }
  }
  // Parents before children wherever the caller listed them so: a child's
  // `near` is then already seated.
  let ring = 0;
  for (const spec of specs) {
    const held = sim.nodes.get(spec.id);
    if (held) {
      held.r = spec.r;
      continue;
    }
    changed = true;
    const near = spec.near ? sim.nodes.get(spec.near) : undefined;
    let x: number;
    let y: number;
    if (near) {
      const angle = rand() * Math.PI * 2;
      const d = near.r + spec.r + 20 + rand() * 20;
      x = near.x + Math.cos(angle) * d;
      y = near.y + Math.sin(angle) * d;
    } else {
      const angle = ring * 2.399963; // the golden angle: an even spread however many there are
      const d = 60 + 40 * Math.sqrt(ring);
      x = Math.cos(angle) * d + (rand() - 0.5) * 4;
      y = Math.sin(angle) * d + (rand() - 0.5) * 4;
      ring++;
    }
    sim.nodes.set(spec.id, { id: spec.id, x, y, vx: 0, vy: 0, r: spec.r, fixed: false });
  }
  const linkKey = (l: PLink) => `${l.a}>${l.b}:${l.len}:${l.k}`;
  const kept = links.filter((l) => sim.nodes.has(l.a) && sim.nodes.has(l.b));
  if (!changed) {
    const before = new Set(sim.links.map(linkKey));
    changed = kept.length !== sim.links.length || kept.some((l) => !before.has(linkKey(l)));
  }
  sim.links = kept;
  if (changed) reheat(sim);
  return changed;
}

/**
 * Advance one tick. Returns whether anything is still moving, so the caller
 * can stop asking for frames once it is not.
 */
export function step(sim: Sim): boolean {
  if (sim.alpha < ALPHA_MIN) return false;
  const nodes = [...sim.nodes.values()];
  const a = sim.alpha;
  const fx = new Float64Array(nodes.length);
  const fy = new Float64Array(nodes.length);
  const index = new Map<string, number>();
  nodes.forEach((n, i) => index.set(n.id, i));

  // Repulsion and collision: every pair, which is fine for the few hundred
  // tasks a board holds.
  for (let i = 0; i < nodes.length; i++) {
    const p = nodes[i]!;
    for (let j = i + 1; j < nodes.length; j++) {
      const q = nodes[j]!;
      let dx = q.x - p.x;
      let dy = q.y - p.y;
      let d2 = dx * dx + dy * dy;
      if (d2 > CUTOFF * CUTOFF) continue;
      if (d2 < 0.01) {
        // Exactly on top of each other: pick a direction from the ids, not at random, so a test is repeatable.
        dx = i - j || 1;
        dy = 1;
        d2 = dx * dx + dy * dy;
      }
      const d = Math.sqrt(d2);
      let f = REPULSION / d2;
      const overlap = p.r + q.r + 6 - d;
      if (overlap > 0) f += overlap * 0.5;
      const ux = dx / d;
      const uy = dy / d;
      fx[i]! -= ux * f;
      fy[i]! -= uy * f;
      fx[j]! += ux * f;
      fy[j]! += uy * f;
    }
  }

  for (const l of sim.links) {
    const i = index.get(l.a);
    const j = index.get(l.b);
    if (i === undefined || j === undefined) continue;
    const p = nodes[i]!;
    const q = nodes[j]!;
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const stretch = d - (p.r + q.r + l.len);
    const f = stretch * l.k * 0.5;
    fx[i]! += (dx / d) * f;
    fy[i]! += (dy / d) * f;
    fx[j]! -= (dx / d) * f;
    fy[j]! -= (dy / d) * f;
  }

  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    if (n.fixed) {
      n.vx = 0;
      n.vy = 0;
      continue;
    }
    n.vx = (n.vx + (fx[i]! - n.x * GRAVITY) * a) * DAMPING;
    n.vy = (n.vy + (fy[i]! - n.y * GRAVITY) * a) * DAMPING;
    const speed = Math.hypot(n.vx, n.vy);
    if (speed > MAX_SPEED) {
      n.vx *= MAX_SPEED / speed;
      n.vy *= MAX_SPEED / speed;
    }
    n.x += n.vx;
    n.y += n.vy;
  }

  sim.alpha *= COOLING;
  return sim.alpha >= ALPHA_MIN;
}

/** The box around every node, for fitting the view to it. Null when there are none. */
export function bounds(sim: Sim): { x0: number; y0: number; x1: number; y1: number } | null {
  let box: { x0: number; y0: number; x1: number; y1: number } | null = null;
  for (const n of sim.nodes.values()) {
    if (!box) box = { x0: n.x - n.r, y0: n.y - n.r, x1: n.x + n.r, y1: n.y + n.r };
    else {
      box.x0 = Math.min(box.x0, n.x - n.r);
      box.y0 = Math.min(box.y0, n.y - n.r);
      box.x1 = Math.max(box.x1, n.x + n.r);
      box.y1 = Math.max(box.y1, n.y + n.r);
    }
  }
  return box;
}
